// ── MessageBubble ──────────────────────────────────────────────────
//
// Renders a single message — direction-styled, type-aware. Handles
// all 10 supported types. Right-click → copy text (we keep the menu
// minimal for the MVP; richer actions defer to Phase 8b).
//
// Media types currently use the bubble even when mediaUrl is the
// `meta-stub:<id>` sentinel from the Phase 5 storage stub — they
// fall back to a "media unavailable" placeholder so the inbox still
// shows the message structure.

import { Download, ExternalLink, FileText, MapPin } from 'lucide-react';
import { StatusTicks } from './StatusTicks';
import { formatMessageTime } from '../utils/time-format';
import type { WhatsappMessage } from '../api/whatsapp.api';

const OUTBOUND_BG = 'bg-[#DCF8C6]'; // WhatsApp-ish pale green
const INBOUND_BG = 'bg-white';

export function MessageBubble({ message }: { message: WhatsappMessage }) {
  const isOutbound = message.direction === 'OUTBOUND';
  const content: any = message.content ?? {};

  return (
    <div className={`flex w-full ${isOutbound ? 'justify-end' : 'justify-start'} px-2`}>
      <div
        className={`relative max-w-[78%] rounded-lg px-2.5 py-1.5 shadow-sm ${
          isOutbound ? `${OUTBOUND_BG} text-slate-800` : `${INBOUND_BG} text-slate-800 border border-gray-100`
        } ${message.status === 'FAILED' ? 'border border-red-200' : ''}`}
      >
        <BubbleBody message={message} content={content} />

        {/* Footer: timestamp + status ticks (outbound only) */}
        <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-slate-500">
          <span>{formatMessageTime(message.timestamp)}</span>
          {isOutbound && <StatusTicks status={message.status} size={12} />}
        </div>

        {message.errorMessage && (
          <div className="mt-1 text-[11px] text-red-600">
            {message.errorMessage}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Body switch ────────────────────────────────────────────────────

function BubbleBody({ message, content }: { message: WhatsappMessage; content: any }) {
  switch (message.messageType) {
    case 'TEXT':
      return <TextBody body={content?.text?.body ?? message.bodyPreview ?? ''} />;

    case 'IMAGE':
      return <ImageBody mediaUrl={message.mediaUrl} caption={content?.image?.caption} />;

    case 'VIDEO':
      return <VideoBody mediaUrl={message.mediaUrl} caption={content?.video?.caption} />;

    case 'AUDIO':
      return <AudioBody mediaUrl={message.mediaUrl} />;

    case 'DOCUMENT':
      return (
        <DocumentBody
          mediaUrl={message.mediaUrl}
          filename={content?.document?.filename}
          caption={content?.document?.caption}
          mimeType={message.mediaMimeType}
          size={message.mediaSize}
        />
      );

    case 'LOCATION':
      return (
        <LocationBody
          lat={content?.location?.latitude}
          lng={content?.location?.longitude}
          name={content?.location?.name}
          address={content?.location?.address}
        />
      );

    case 'TEMPLATE':
      return <TemplateBody content={content} preview={message.bodyPreview} />;

    case 'INTERACTIVE':
      return <InteractiveBody content={content} />;

    case 'CONTACTS':
      return <ContactsBody contacts={content?.contacts ?? []} />;

    case 'REACTION':
      return (
        <span className="text-2xl">{content?.reaction?.emoji ?? '👍'}</span>
      );

    case 'STICKER':
      return (
        <div className="text-xs italic text-slate-500">
          [sticker]
        </div>
      );

    default:
      return (
        <div className="text-xs italic text-slate-500">
          [{(message.messageType ?? 'unknown').toLowerCase()}] — Preview: {message.bodyPreview ?? '—'}
        </div>
      );
  }
}

// ── Per-type renderers ─────────────────────────────────────────────

function TextBody({ body }: { body: string }) {
  return (
    <div className="whitespace-pre-wrap break-words text-[14px] leading-relaxed">
      {autoLink(body)}
    </div>
  );
}

function ImageBody({ mediaUrl, caption }: { mediaUrl: string | null; caption?: string }) {
  const isStub = !mediaUrl || mediaUrl.startsWith('meta-stub:');
  return (
    <div className="space-y-1">
      {isStub ? (
        <MediaPlaceholder label="Image" hint="Media storage not configured" />
      ) : (
        <img
          src={mediaUrl}
          alt={caption ?? 'image'}
          className="max-h-[300px] max-w-[280px] rounded-md object-cover"
          loading="lazy"
        />
      )}
      {caption && <div className="text-[13px]">{autoLink(caption)}</div>}
    </div>
  );
}

function VideoBody({ mediaUrl, caption }: { mediaUrl: string | null; caption?: string }) {
  const isStub = !mediaUrl || mediaUrl.startsWith('meta-stub:');
  return (
    <div className="space-y-1">
      {isStub ? (
        <MediaPlaceholder label="Video" hint="Media storage not configured" />
      ) : (
        <video
          src={mediaUrl}
          controls
          preload="metadata"
          className="max-h-[300px] max-w-[280px] rounded-md"
        />
      )}
      {caption && <div className="text-[13px]">{autoLink(caption)}</div>}
    </div>
  );
}

function AudioBody({ mediaUrl }: { mediaUrl: string | null }) {
  const isStub = !mediaUrl || mediaUrl.startsWith('meta-stub:');
  if (isStub) return <MediaPlaceholder label="Audio" hint="Media storage not configured" />;
  return (
    <audio src={mediaUrl} controls preload="metadata" className="max-w-[260px]" />
  );
}

function DocumentBody({
  mediaUrl,
  filename,
  caption,
  mimeType,
  size,
}: {
  mediaUrl: string | null;
  filename?: string;
  caption?: string;
  mimeType: string | null;
  size: number | null;
}) {
  const isStub = !mediaUrl || mediaUrl.startsWith('meta-stub:');
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-white/60 px-3 py-2">
        <FileText className="h-6 w-6 text-slate-500" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-slate-800">
            {filename ?? 'Document'}
          </div>
          <div className="text-[11px] text-slate-500">
            {mimeType ?? 'unknown'}
            {size != null && ` · ${formatBytes(size)}`}
          </div>
        </div>
        {!isStub && (
          <a
            href={mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-indigo-600"
            title="Download"
            aria-label="Download document"
          >
            <Download className="h-4 w-4" />
          </a>
        )}
      </div>
      {caption && <div className="text-[13px]">{autoLink(caption)}</div>}
    </div>
  );
}

function LocationBody({
  lat,
  lng,
  name,
  address,
}: {
  lat?: number;
  lng?: number;
  name?: string;
  address?: string;
}) {
  const hasCoords = typeof lat === 'number' && typeof lng === 'number';
  const url = hasCoords
    ? `https://www.google.com/maps?q=${lat},${lng}`
    : null;
  return (
    <a
      href={url ?? '#'}
      target={url ? '_blank' : undefined}
      rel="noopener noreferrer"
      className="block min-w-[200px] rounded-md border border-gray-200 bg-white/60 p-2.5 hover:bg-white"
    >
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4 text-red-500" />
        <span className="text-[13px] font-medium">{name ?? 'Location'}</span>
      </div>
      {address && <div className="mt-0.5 text-[11px] text-slate-500">{address}</div>}
      {hasCoords && (
        <div className="mt-1 text-[11px] text-slate-400">
          {lat.toFixed(4)}, {lng.toFixed(4)}
          <ExternalLink className="inline-block h-3 w-3 ml-1 align-text-bottom" />
        </div>
      )}
    </a>
  );
}

function TemplateBody({ content, preview }: { content: any; preview?: string | null }) {
  const name = content?.template?.name ?? '';
  return (
    <div>
      <div className="mb-1 inline-flex items-center gap-1 rounded bg-slate-200/60 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-600">
        Template
        {name && <span className="font-normal normal-case">· {name}</span>}
      </div>
      <div className="whitespace-pre-wrap text-[14px] leading-relaxed">
        {preview ?? autoLink(extractTemplateBody(content))}
      </div>
    </div>
  );
}

function InteractiveBody({ content }: { content: any }) {
  const interactive = content?.interactive ?? {};
  const subType = interactive.type ?? 'button';
  const bodyText: string =
    interactive?.body?.text ??
    interactive?.button_reply?.title ??
    interactive?.list_reply?.title ??
    '';

  return (
    <div className="min-w-[200px]">
      <div className="text-[14px]">{autoLink(bodyText)}</div>
      {subType === 'button' && Array.isArray(interactive?.action?.buttons) && (
        <div className="mt-1.5 flex flex-col gap-1">
          {interactive.action.buttons.map((b: any, i: number) => (
            <div
              key={i}
              className="rounded border border-gray-200 bg-white/80 px-2 py-1 text-center text-[12px] text-indigo-600"
            >
              {b?.reply?.title ?? b?.title ?? '—'}
            </div>
          ))}
        </div>
      )}
      {subType === 'list' && interactive?.action?.button && (
        <div className="mt-1.5 rounded border border-gray-200 bg-white/80 px-2 py-1 text-center text-[12px] text-indigo-600">
          {interactive.action.button}
        </div>
      )}
    </div>
  );
}

function ContactsBody({ contacts }: { contacts: any[] }) {
  if (contacts.length === 0) return <span className="italic text-slate-500">[contact]</span>;
  const first = contacts[0];
  const name = first?.name?.formatted_name ?? first?.name?.first_name ?? 'Contact';
  const phone = first?.phones?.[0]?.phone ?? null;
  return (
    <div className="min-w-[200px] rounded border border-gray-200 bg-white/80 p-2">
      <div className="text-[13px] font-medium">👤 {name}</div>
      {phone && <div className="text-[12px] text-slate-500">{phone}</div>}
      {contacts.length > 1 && (
        <div className="mt-1 text-[11px] text-slate-400">+{contacts.length - 1} more</div>
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

function MediaPlaceholder({ label, hint }: { label: string; hint: string }) {
  return (
    <div className="flex h-20 w-[200px] flex-col items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 text-[11px] text-slate-500">
      <div className="font-medium text-slate-600">[{label}]</div>
      <div className="mt-0.5 italic">{hint}</div>
    </div>
  );
}

/**
 * Replace bare http(s) URLs in text with clickable links. Intentionally
 * minimal — no markdown, no @mentions; WhatsApp's own formatting (bold
 * via *...*, italic via _..._) is faithful when needed but not done
 * here to keep the renderer trivial.
 */
function autoLink(text: string): React.ReactNode {
  if (!text) return null;
  const re = /(https?:\/\/[^\s]+)/g;
  const parts: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <a
        key={key++}
        href={m[0]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-indigo-600 underline hover:text-indigo-700"
      >
        {m[0]}
      </a>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length > 0 ? parts : text;
}

function extractTemplateBody(content: any): string {
  // Best-effort: pull the BODY component if present in the outbound payload.
  const components = content?.template?.components;
  if (Array.isArray(components)) {
    const body = components.find((c: any) => String(c?.type).toUpperCase() === 'BODY');
    if (body?.parameters) {
      return body.parameters.map((p: any) => p?.text ?? '').join(' ');
    }
  }
  return '';
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
